import { useEffect, useState } from "react";

import { ArrowDownCircle, ArrowUpCircle, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { useCorrectBatchConsumption } from "~/hooks/production";
import { useToast } from "~/hooks/use-toast";
import type { ProductionBatchIngredient } from "~/types/production";

interface CorrectConsumptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchId: string;
  ingredient: ProductionBatchIngredient | null;
  onSuccess: () => void;
}

export function CorrectConsumptionDialog({
  open,
  onOpenChange,
  batchId,
  ingredient,
  onSuccess,
}: CorrectConsumptionDialogProps) {
  const { toast } = useToast();
  const correct = useCorrectBatchConsumption();

  const currentQty = ingredient?.actual_quantity ?? 0;
  const isOil = !!ingredient?.oil_type_id;
  const code = isOil
    ? ingredient?.oil_type?.code
    : ingredient?.raw_material?.code;
  const name = isOil
    ? ingredient?.oil_type?.name
    : ingredient?.raw_material?.name;
  const unit = isOil ? "kg" : ingredient?.raw_material?.unit || "units";

  const [newQuantity, setNewQuantity] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setNewQuantity(String(currentQty));
      setReason("");
    }
  }, [open, currentQty]);

  const newQtyNum = parseFloat(newQuantity);
  const hasValidQty = !Number.isNaN(newQtyNum) && newQtyNum >= 0;
  const delta = hasValidQty ? newQtyNum - currentQty : 0;
  const canSubmit =
    !!ingredient &&
    hasValidQty &&
    newQtyNum !== currentQty &&
    reason.trim().length > 0 &&
    !correct.isPending;

  const handleConfirm = () => {
    if (!ingredient) return;
    correct.mutate(
      {
        pbiId: ingredient.id,
        batchId,
        newQuantity: newQtyNum,
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          toast({
            title: "Consumo corregido",
            description: `${name ?? "Ingrediente"} actualizado a ${newQtyNum} ${unit}.`,
          });
          onOpenChange(false);
          onSuccess();
        },
        onError: (error) => {
          toast({
            title: "No se pudo corregir el consumo",
            description: (error as Error).message,
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Corregir consumo</DialogTitle>
          <DialogDescription>
            Ajusta la cantidad realmente consumida de este ingrediente. El stock
            del inventario y el costo del lote se recalculan automáticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/50 p-3 text-sm">
            <div className="font-medium">{name ?? "-"}</div>
            {code && (
              <div className="text-xs text-muted-foreground">{code}</div>
            )}
            <div className="mt-2 flex justify-between">
              <span className="text-muted-foreground">Consumo actual</span>
              <span className="tabular-nums font-medium">
                {currentQty} {unit}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="corrected-qty">Cantidad real corregida</Label>
            <div className="flex items-center gap-2">
              <Input
                id="corrected-qty"
                type="number"
                step="any"
                min="0"
                value={newQuantity}
                onChange={(e) => setNewQuantity(e.target.value)}
                placeholder="0"
                className="h-9"
              />
              <span className="w-16 shrink-0 text-sm text-muted-foreground">
                {unit}
              </span>
            </div>
          </div>

          {hasValidQty && delta !== 0 && (
            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
              {delta < 0 ? (
                <>
                  <ArrowUpCircle className="h-4 w-4 shrink-0 text-green-700" />
                  <span>
                    Se devolverán{" "}
                    <span className="font-medium tabular-nums text-green-700">
                      {Math.abs(delta)} {unit}
                    </span>{" "}
                    al inventario.
                  </span>
                </>
              ) : (
                <>
                  <ArrowDownCircle className="h-4 w-4 shrink-0 text-red-700" />
                  <span>
                    Se descontarán{" "}
                    <span className="font-medium tabular-nums text-red-700">
                      {delta} {unit}
                    </span>{" "}
                    del inventario.
                  </span>
                </>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="corrected-reason">Motivo</Label>
            <Textarea
              id="corrected-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo de la corrección (error de registro, reconteo, etc.)"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={correct.isPending}
          >
            Cancelar
          </Button>
          <Button disabled={!canSubmit} onClick={handleConfirm}>
            {correct.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Corregir consumo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
